import { useState, useCallback, useRef, useEffect } from "react";
import axios, { CancelTokenSource } from "axios";
import { v4 as uuidv4 } from "uuid";
import { getCancelToken, request } from "../../../services/cpln";
import {
  LogEntry,
  LogsQueryResponse,
  UseLogsQueryOptions,
  UseLogsQueryReturn,
} from "../types";
import { processLabels } from "../utils/labelProcessor";
import { resolveExpressionPair } from "../../datepicker";

/**
 * Hook to execute log queries via HTTP API
 */
export function useLogsQuery(options: UseLogsQueryOptions): UseLogsQueryReturn {
  const { org, logsEndpoint, timezone } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const execute = useCallback(
    async (query: string, from: string, to: string): Promise<LogEntry[] | null> => {
      // Cancel any pending request
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Cancelled");
      }
      cancelTokenRef.current = getCancelToken();

      setIsLoading(true);
      setError(null);

      try {
        // Resolve from/to together to handle start/end cross-references
        const resolved = resolveExpressionPair(from, to, timezone);
        const startNs = resolved.from
          ? String(resolved.from.getTime() * 1000000)
          : String(Date.now() * 1000000);
        const endNs = resolved.to
          ? String(resolved.to.getTime() * 1000000)
          : String(Date.now() * 1000000);

        const params = new URLSearchParams({
          query,
          start: startNs,
          end: endNs,
          limit: "1000",
        });

        const response = await request<LogsQueryResponse>({
          service: "logs",
          url: `/logs/org/${org}/loki/api/v1/query_range?${params.toString()}`,
          method: "get",
          cancelToken: cancelTokenRef.current.token,
        });

        const entries: LogEntry[] = [];

        if (response.data?.data?.result) {
          for (const stream of response.data.data.result) {
            const labels = processLabels(stream.stream);

            for (const [timestampNs, log] of stream.values) {
              // Convert nanoseconds to milliseconds
              const timestampMs = Math.floor(Number(timestampNs) / 1000000);

              entries.push({
                id: uuidv4(),
                timestamp: timestampMs,
                labels,
                log,
              });
            }
          }
        }

        setIsLoading(false);
        return entries;
      } catch (e: any) {
        // If cancelled by user, silently reset state
        if (axios.isCancel(e)) {
          setIsLoading(false);
          return null;
        }

        // Try different locations where API error message might be
        let errorMessage = e?.response?.data?.message  // Standard API format
          || e?.response?.data?.error                  // Loki API format
          || (typeof e?.response?.data === "string" ? e.response.data : null)  // Plain string response
          || (e instanceof Error ? e.message : String(e));
        const err = new Error(errorMessage);
        setError(err);
        setIsLoading(false);
        throw err;
      }
    },
    [org, logsEndpoint, timezone]
  );

  const cancel = useCallback(() => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Cancelled");
      cancelTokenRef.current = null;
    }
    setIsLoading(false);
    setError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Cancelled");
      }
    };
  }, []);

  return {
    execute,
    cancel,
    isLoading,
    error,
  };
}
